import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";
import { RupiahInput } from "@/components/RupiahInput";

const n = (v) => parseFloat(v || 0) || 0;

function AdsControlChart({ rows }) {
  const data = (rows || []).map((r) => ({ ...r, sales: n(r.sales), ads: n(r.ads) }));
  const max = Math.max(1, ...data.flatMap((r) => [r.sales, r.ads]));
  const w = 1000, h = 170, px = 34, py = 16, bottom = 28;
  const innerW = w - px * 2;
  const innerH = h - py - bottom;
  const point = (value, i) => ({
    x: px + (data.length <= 1 ? innerW / 2 : (i / (data.length - 1)) * innerW),
    y: py + innerH - (value / max) * innerH,
  });
  const salesPath = data.map((r, i) => { const p = point(r.sales, i); return `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
  const adsPath = data.map((r, i) => { const p = point(r.ads, i); return `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
  const labelIdx = new Set([0, Math.floor((data.length - 1) / 2), data.length - 1]);
  const fmtDate = (s) => s ? new Date(`${s}T00:00:00`).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : "";

  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 180, display: "block" }} role="img" aria-label="Grafik omzet dan biaya iklan">
        {[0, .25, .5, .75, 1].map((t) => {
          const y = py + innerH - t * innerH;
          return <g key={t}><line x1={px} y1={y} x2={w-px} y2={y} stroke="currentColor" opacity="0.07" /><text x={px} y={y-4} fontSize="10" opacity="0.5">{formatIDR(max*t)}</text></g>;
        })}
        {salesPath && <path d={salesPath} fill="none" stroke="#24313F" strokeWidth="2.5" />}
        {adsPath && <path d={adsPath} fill="none" stroke="#D97706" strokeWidth="2.5" />}
        {data.map((r, i) => { const p = point(r.sales, i); return <g key={r.date}>{labelIdx.has(i) && <text x={p.x} y={h-7} textAnchor={i===0?"start":i===data.length-1?"end":"middle"} fontSize="10" opacity="0.58">{fmtDate(r.date)}</text>}</g>; })}
      </svg>
      <div style={{ display: "flex", gap: 16, justifyContent: "flex-end", marginTop: -4 }}>
        <span className="hint"><b style={{ color: "#24313F" }}>━</b> Omzet</span>
        <span className="hint"><b style={{ color: "#D97706" }}>━</b> Biaya Iklan</span>
      </div>
    </div>
  );
}

export default function AdsPanel() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [rows, setRows] = useState([]);
  const [sources, setSources] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [days, setDays] = useState(30);
  const [storeMappingId, setStoreMappingId] = useState("");
  const [amount, setAmount] = useState("");
  const [adsDate, setAdsDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [period, setPeriod] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [adsStoreFilter, setAdsStoreFilter] = useState("");
  const [adsPlatformFilter, setAdsPlatformFilter] = useState("");
  const [adsFrom, setAdsFrom] = useState("");
  const [adsTo, setAdsTo] = useState("");
  const [dashFrom, setDashFrom] = useState("");
  const [dashTo, setDashTo] = useState("");

  const load = () => api.get("/marketplace/ads").then(({ data }) => setRows(data)).catch(() => {});
  const loadSources = () => api.get("/marketplace/ads/sources").then(({ data }) => setSources(data)).catch(() => {});
  const loadDashboard = useCallback(() => { const q = (dashFrom && dashTo) ? `from_date=${dashFrom}&to_date=${dashTo}` : `days=${days}`; return api.get(`/marketplace/ads/dashboard?${q}`).then(({ data }) => setDashboard(data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Dashboard iklan gagal dimuat.")); }, [dashFrom, dashTo, days]);
  useEffect(() => { load(); loadSources(); }, []);
  useEffect(() => { if (isOwner) loadDashboard(); }, [isOwner, loadDashboard]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice("");
    if (!storeMappingId || !amount || !adsDate) { setError("Sumber iklan, jumlah, dan tanggal iklan wajib diisi."); return; }
    setBusy(true);
    try {
      await api.post("/marketplace/ads", {
        idempotency_key: (crypto.randomUUID ? crypto.randomUUID() : `ADS-${Date.now()}`),
        store_mapping_id: storeMappingId,
        amount: Number(amount),
        ads_date: adsDate,
        period_reference: period.trim() || null,
        input_source: "MANUAL",
      });
      setStoreMappingId(""); setAmount(""); setAdsDate(new Date().toLocaleDateString("en-CA")); setPeriod("");
      setNotice("Debit iklan berhasil dicatat.");
      await Promise.all([load(), loadDashboard()]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const correctAds = async (r) => {
    if (r.status === "CORRECTED") return;
    const reason = window.prompt(`Alasan koreksi Debit Iklan ${formatIDR(r.amount)} (${r.source_shop})?`);
    if (!reason?.trim()) return;
    if (!window.confirm("Transaksi lama akan dibalik ke wallet dengan komposisi dana persis transaksi awal. Setelah itu form akan diisi ulang agar Anda mencatat nilai yang benar. Lanjut?")) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await api.post(`/marketplace/ads/${r.id}/correct`, { reason: reason.trim() });
      const source = sources.find((s) => s.source_shop === r.source_shop && s.wallet_account_id === r.account_id)
        || sources.find((s) => s.source_shop === r.source_shop);
      setStoreMappingId(source?.store_mapping_id || "");
      setAmount(String(Math.abs(n(r.amount))));
      setAdsDate((r.ads_date || "").slice(0, 10));
      setPeriod(r.period_reference || "");
      setNotice("Debit lama sudah dibalik dan ditandai CORRECTED. Periksa form di atas, ubah yang salah, lalu Catat Debit Iklan kembali.");
      await Promise.all([load(), loadDashboard()]);
      setTimeout(() => document.querySelector('[data-testid="ads-form-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const ratio = n(dashboard?.ads_ratio);
  const controlLabel = ratio === 0 ? "Belum ada biaya iklan" : ratio <= 10 ? "Efisien" : ratio <= 15 ? "Perlu dipantau" : "Terlalu berat";
  const controlColor = ratio === 0 ? "var(--muted)" : ratio <= 10 ? "#15803D" : ratio <= 15 ? "#B45309" : "#DC2626";
  const storeRows = useMemo(() => dashboard?.stores || [], [dashboard]);

  const visibleRows = useMemo(() => (rows || []).filter((r) => r.input_source !== "CORRECTION_REVERSAL"), [rows]);
  const adsStoreOptions = useMemo(() => {
    const m = new Map();
    visibleRows.forEach((r) => {
      const key = r.store_id || r.source_shop || "?";
      if (!m.has(key)) m.set(key, r.master_store || r.source_shop || "?");
    });
    return [...m.entries()].map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleRows]);
  const shownAds = useMemo(() => visibleRows.filter((r) => {
    if (adsStoreFilter && (r.store_id || r.source_shop || "?") !== adsStoreFilter) return false;
    if (adsPlatformFilter && (r.marketplace || "") !== adsPlatformFilter) return false;
    const d = (r.ads_date || "").slice(0, 10);
    if (adsFrom && (!d || d < adsFrom)) return false;
    if (adsTo && (!d || d > adsTo)) return false;
    return true;
  }), [visibleRows, adsStoreFilter, adsPlatformFilter, adsFrom, adsTo]);
  const shownAdsTotal = useMemo(() => shownAds.reduce((s, r) => s + (r.status === "CORRECTED" ? 0 : n(r.amount)), 0), [shownAds]);

  if (user && !isOwner) {
    return (
      <div className="app-shell"><AppTopBar active="ads" />
        <main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main>
      </div>
    );
  }

  const card = { background: "#fff", border: "1px solid var(--border)", borderRadius: 10, padding: "11px 12px", minHeight: 76 };

  return (
    <div className="app-shell">
      <AppTopBar active="ads" />
      <main className="content">
        <div style={{ width: "100%", maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
            <div><h1 style={{ margin: "0 0 4px", fontSize: 24 }}>Dashboard Iklan</h1><p className="hint" style={{ margin: 0 }}>Kontrol biaya iklan terhadap omzet marketplace.</p></div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"flex-end" }}>
              <div><label className="field-label">DARI TANGGAL</label><input className="field-input" type="date" data-testid="ads-dash-from" value={dashFrom} max={dashTo || undefined} onChange={(e)=>setDashFrom(e.target.value)} /></div>
              <div><label className="field-label">SAMPAI TANGGAL</label><input className="field-input" type="date" data-testid="ads-dash-to" value={dashTo} min={dashFrom || undefined} onChange={(e)=>setDashTo(e.target.value)} /></div>
              <div><label className="field-label">PERIODE</label><select className="field-input" data-testid="ads-dash-period" value={dashFrom&&dashTo?"":String(days)} onChange={(e)=>{ setDashFrom(""); setDashTo(""); setDays(parseInt(e.target.value,10)); }}>{dashFrom&&dashTo&&<option value="">Rentang custom</option>}<option value={7}>7 hari</option><option value={30}>30 hari</option><option value={60}>60 hari</option><option value={90}>90 hari</option></select></div>
            </div>
          </div>

          {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
          {notice && <div className="alert-info" data-testid="notice-banner">{notice}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8, marginBottom: 8 }}>
            <div style={card}><div className="meta-label">OMZET MARKETPLACE</div><div className="mono" style={{ fontSize: 19, fontWeight: 700, marginTop: 5 }}>{dashboard ? formatIDR(dashboard.total_sales) : "-"}</div><div className="hint">Periode terpilih</div></div>
            <div style={card}><div className="meta-label">BIAYA IKLAN</div><div className="mono" style={{ fontSize: 19, fontWeight: 700, marginTop: 5 }}>{dashboard ? formatIDR(dashboard.total_ads) : "-"}</div><div className="hint">Debit iklan tercatat</div></div>
            <div style={card}><div className="meta-label">ADS COST RATIO</div><div className="mono" style={{ fontSize: 19, fontWeight: 700, marginTop: 5 }}>{dashboard ? `${Number(dashboard.ads_ratio).toFixed(2)}%` : "-"}</div><div className="hint" style={{ color: controlColor }}>{controlLabel}</div></div>
            <div style={card}><div className="meta-label">ROAS BISNIS</div><div className="mono" style={{ fontSize: 19, fontWeight: 700, marginTop: 5 }}>{dashboard ? `${Number(dashboard.roas).toFixed(2)}x` : "-"}</div><div className="hint">Omzet ÷ biaya iklan</div></div>
          </div>

          <section className="panel" style={{ marginBottom: 8, padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 2 }}><div><h2 className="panel-title" style={{ marginBottom: 2 }}>Omzet vs Biaya Iklan</h2><div className="hint">Perbandingan harian pada periode yang sama.</div></div><b>{dashboard ? `${Number(dashboard.ads_ratio).toFixed(2)}%` : "-"}</b></div>
            <AdsControlChart rows={dashboard?.daily || []} />
          </section>

          <section className="panel" style={{ marginBottom: 8, padding: 14 }}>
            <div className="panel-head"><div><h2 className="panel-title" style={{ marginBottom: 2 }}>Kontrol per Toko</h2><div className="hint">Toko dengan rasio iklan tinggi langsung terlihat.</div></div></div>
            <div className="table-scroll"><table className="data-table"><thead><tr><th>Toko</th><th>Marketplace</th><th>Omzet</th><th>Biaya Iklan</th><th>Ads Ratio</th><th>ROAS</th><th>Status</th></tr></thead><tbody>
              {storeRows.length === 0 && <tr><td colSpan={7} className="empty-cell">Belum ada data iklan / penjualan pada periode ini.</td></tr>}
              {storeRows.map((r, i) => { const rr=n(r.ads_ratio); const status=rr===0?"-":rr<=10?"EFISIEN":rr<=15?"PANTAU":"BERAT"; return <tr key={`${r.store_id || "x"}-${i}`}><td><b>{r.store}</b></td><td>{r.marketplace || "-"}</td><td className="mono">{formatIDR(r.sales)}</td><td className="mono">{formatIDR(r.ads)}</td><td className="mono"><b>{Number(r.ads_ratio).toFixed(2)}%</b></td><td className="mono">{Number(r.roas).toFixed(2)}x</td><td><span className={`status-pill ${status==="EFISIEN"?"on":status==="PANTAU"?"warn":"off"}`}>{status}</span></td></tr>; })}
            </tbody></table></div>
          </section>

          <section className="panel" data-testid="ads-form-panel" style={{ marginBottom: 8 }}>
            <div className="panel-head"><h2 className="panel-title">Catat Debit Iklan</h2></div>
            <p className="field-help" style={{ marginBottom: 12 }}>
              Debit iklan sudah otomatis terjadi di wallet. Dana dikonsumsi PROFIT dulu, sisanya MODAL. UNCLASSIFIED tidak pernah dikonsumsi otomatis (jika PROFIT+MODAL kurang → ditahan untuk review).
            </p>
            <form onSubmit={submit}>
              <div className="form-row" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 300 }}><label className="field-label">Sumber Iklan (Toko — Marketplace — Wallet)</label><select className="field-input" value={storeMappingId} onChange={(e) => setStoreMappingId(e.target.value)} data-testid="ads-source-select"><option value="">— Pilih sumber —</option>{sources.map((s) => <option key={s.store_mapping_id} value={s.store_mapping_id} disabled={!s.has_wallet}>{`${s.master_store || "?"} — ${s.marketplace || "-"} — ${s.has_wallet ? s.wallet_name : "WALLET BELUM DISET"}`}</option>)}</select></div>
                <div style={{ width: 180 }}><label className="field-label">Jumlah (Rp)</label><RupiahInput className="field-input" value={amount} onChange={setAmount} placeholder="0" /></div>
                <div style={{ width: 165 }}><label className="field-label">Tanggal Iklan *</label><input className="field-input" type="date" required value={adsDate} onChange={(e) => setAdsDate(e.target.value)} /></div>
                <div style={{ width: 190 }}><label className="field-label">Periode / Referensi</label><input className="field-input" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="mis. Sep-2026" /></div>
              </div>
              <div className="form-actions"><button className="btn-primary" type="submit" disabled={busy}>{busy ? "Memproses..." : "Catat Debit Iklan"}</button></div>
            </form>
          </section>

          <section className="panel" data-testid="ads-list-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
              <h2 className="panel-title" style={{ margin: 0 }}>Riwayat Debit Iklan</h2>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select className="field-input" style={{ width: "auto", minWidth: 150, padding: "4px 8px" }} value={adsStoreFilter} onChange={(e) => setAdsStoreFilter(e.target.value)} data-testid="ads-store-filter">
                  <option value="">Semua Toko</option>
                  {adsStoreOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <select className="field-input" style={{ width: "auto", minWidth: 140, padding: "4px 8px" }} value={adsPlatformFilter} onChange={(e) => setAdsPlatformFilter(e.target.value)} data-testid="ads-platform-filter">
                  <option value="">Semua Platform</option><option value="SHOPEE">SHOPEE</option><option value="TIKTOK">TIKTOK</option>
                </select>
                <input className="field-input" type="date" style={{ width: 150, padding: "4px 8px" }} value={adsFrom} onChange={(e) => setAdsFrom(e.target.value)} data-testid="ads-date-from" title="Dari tanggal" />
                <input className="field-input" type="date" style={{ width: 150, padding: "4px 8px" }} value={adsTo} onChange={(e) => setAdsTo(e.target.value)} data-testid="ads-date-to" title="Sampai tanggal" />
                <span className="hint" data-testid="ads-count">{shownAds.length} entri · {formatIDR(shownAdsTotal)}</span>
              </div>
            </div>
            <div className="table-scroll"><table className="data-table" data-testid="ads-table"><thead><tr><th>Tanggal</th><th>Toko</th><th>Marketplace</th><th>Jumlah</th><th>PROFIT</th><th>MODAL</th><th>Status</th><th>Aksi</th></tr></thead><tbody>
              {shownAds.length === 0 && <tr><td colSpan={8} className="empty-cell">Belum ada debit iklan.</td></tr>}
              {shownAds.map((r) => <tr key={r.id}><td>{r.ads_date || "-"}</td><td>{r.source_shop}{r.master_store ? ` (${r.master_store})` : ""}</td><td>{r.marketplace || "-"}</td><td>{formatIDR(r.amount)}</td><td>{formatIDR(r.profit_used)}</td><td>{formatIDR(r.modal_used)}</td><td><span className={`status-pill ${r.status === "COMMITTED" ? "on" : "warn"}`}>{r.status}</span>{r.status === "CORRECTED" && r.warning ? <div className="hint">{r.warning}</div> : null}</td><td>{r.status === "CORRECTED" ? <span className="hint">Sudah dikoreksi</span> : <button className="btn-mini" disabled={busy} onClick={() => correctAds(r)}>Koreksi</button>}</td></tr>)}
            </tbody></table></div>
          </section>
        </div>
      </main>
    </div>
  );
}
